import type { Variants } from "framer-motion";
import {
	createContext,
	type FC,
	type ReactNode,
	useContext,
	useMemo,
} from "react";

export interface AnimationConfig {
	/** Whether animations are enabled globally */
	enabled: boolean;
	/** Whether to respect user's prefers-reduced-motion preference */
	respectMotionPreference: boolean;
	/** Animation duration multiplier (1.0 = normal speed) */
	duration: number;
	/** Delay between staggered animations in seconds */
	staggerDelay: number;
}

export interface AnimationConfigContextValue extends AnimationConfig {
	/** Whether animations should actually run (considers both enabled and motion preference) */
	shouldAnimate: boolean;
	/** Calculated duration based on multiplier */
	calculatedDuration: number;
}

const DEFAULT_ANIMATION_CONFIG: AnimationConfig = {
	enabled: true,
	respectMotionPreference: true,
	duration: 1.0,
	staggerDelay: 0.04,
};

const AnimationConfigContext = createContext<
	AnimationConfigContextValue | undefined
>(undefined);

export interface AnimationConfigProviderProps {
	children: ReactNode;
	config?: Partial<AnimationConfig>;
}

export const AnimationConfigProvider: FC<AnimationConfigProviderProps> = ({
	children,
	config = {},
}) => {
	const finalConfig = useMemo(
		() => ({ ...DEFAULT_ANIMATION_CONFIG, ...config }),
		[config],
	);

	const contextValue = useMemo(() => {
		// Check for prefers-reduced-motion
		const prefersReducedMotion =
			typeof window !== "undefined" &&
			window.matchMedia &&
			window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		const shouldAnimate =
			finalConfig.enabled &&
			(!finalConfig.respectMotionPreference || !prefersReducedMotion);

		const calculatedDuration = shouldAnimate ? finalConfig.duration * 0.3 : 0; // Base duration of 0.3s

		return {
			...finalConfig,
			shouldAnimate,
			calculatedDuration,
		};
	}, [finalConfig]);

	return (
		<AnimationConfigContext.Provider value={contextValue}>
			{children}
		</AnimationConfigContext.Provider>
	);
};

// Hooks
export const useAnimationConfig = (): AnimationConfigContextValue => {
	const context = useContext(AnimationConfigContext);
	if (!context) {
		throw new Error(
			"useAnimationConfig must be used within AnimationConfigProvider",
		);
	}
	return context;
};

export const useIsAnimationEnabled = (): boolean => {
	const { shouldAnimate } = useAnimationConfig();
	return shouldAnimate;
};

export const useAnimationDuration = (): number => {
	const { calculatedDuration } = useAnimationConfig();
	return calculatedDuration;
};

export const useAnimationVariants = (): {
	container: Variants;
	item: Variants;
} => {
	const { shouldAnimate, calculatedDuration, staggerDelay } =
		useAnimationConfig();

	return useMemo(
		() => ({
			container: shouldAnimate
				? {
						hidden: {
							transition: {
								staggerChildren: staggerDelay,
								staggerDirection: -1,
								when: "afterChildren",
							},
						},
						show: {
							transition: {
								staggerChildren: staggerDelay,
								staggerDirection: 1,
								when: "beforeChildren",
							},
						},
					}
				: ({} as Variants),
			item: shouldAnimate
				? {
						hidden: {
							opacity: 0,
							transition: { type: "linear", duration: calculatedDuration },
						},
						show: {
							opacity: 1,
							transition: { type: "linear", duration: calculatedDuration },
						},
					}
				: ({} as Variants),
		}),
		[shouldAnimate, calculatedDuration, staggerDelay],
	);
};
